'use client';

import * as React from 'react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Button, Typography, Modal, Select, MenuItem, CircularProgress, Grid, Chip, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useRouter } from 'next/navigation';
import dayjs from 'dayjs';
import SmallCustomCalendar from './small_custom_calendar';
import { FetchAllVenues, FetchVenuesWithAvailability, fetchAllVenueMetadata } from '../lib/firebase/venues';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { trackEvent } from '../lib/heapClient';

interface VenueProps {
    venues?: {
        Attributes: { [key: string]: boolean };
        Neighborhoods?: string[];
    }[];
    isOpen: boolean;
    onClose: () => void;
}

const SearchModal = ({ venues: initialVenues, isOpen, onClose }: VenueProps) => {
    const router = useRouter();
    const [venues, setVenues] = useState<VenueProps['venues']>(initialVenues || []);
    const [isLoading, setIsLoading] = useState(!initialVenues);
    const [date, setDate] = useState<Date | null>(dayjs().add(1, 'week').toDate());
    const [time, setTime] = useState<string | null>('19:00');
    const [partySize, setPartySize] = useState<string>('25');
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
    const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<string[]>([]);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
    const [availableAttributes, setAvailableAttributes] = useState<{ [key: string]: number }>({});
    const [validCombinations, setValidCombinations] = useState<Set<string>>(new Set());
    const [filteredVenues, setFilteredVenues] = useState<VenueProps['venues']>([]);

    // Simplified state - reducing complexity
    const [possibleAttributes, setPossibleAttributes] = useState<string[]>([]);
    const [attributeIcons, setAttributeIcons] = useState<Record<string, string>>({});
    const [iconComponents, setIconComponents] = useState<Record<string, React.ComponentType>>({});

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Hardcoded locations
    const hardcodedLocations = ['Manhattan', 'Queens', 'Brooklyn'];

    const nonLocationTypes = [
        'wedding', 'corporate', 'no minimum spend', 'bar', 'restaurant', 'fancy', 'casual',
        'intimate gathering'
    ];

    const spaceTypes = [...nonLocationTypes].filter(type => type in availableAttributes);

    // Simple function to get neighborhood name as string
    const getNeighborhoodName = (neighborhood: any): string => {
        if (typeof neighborhood === 'string') {
            return neighborhood;
        }

        if (neighborhood && typeof neighborhood === 'object') {
            try {
                // Get all the keys of the object
                const keys = Object.keys(neighborhood);

                // If it has values, return the first value
                if (keys.length > 0) {
                    return String(neighborhood[keys[0]]);
                }
            } catch (err) {
                console.error('Error extracting neighborhood name:', err);
            }
        }

        return String(neighborhood || '');
    };

    // Function to filter venues based on selected attributes and neighborhoods
    const filterVenuesSimple = () => {
        if (!venues) return [];

        return venues.filter(venue => {
            // Get venue attributes
            const venueAttributes = Object.entries(venue.Attributes || {})
                .filter(([_, value]) => value)
                .map(([attr, _]) => attr.toLowerCase());

            // Check if venue has any of the selected attributes
            const hasSelectedAttributes = selectedSpaces.length === 0 ||
                selectedSpaces.some(attr => venueAttributes.includes(attr.toLowerCase()));

            // Check if venue has any of the selected neighborhoods
            const hasSelectedNeighborhoods = selectedNeighborhoods.length === 0 ||
                selectedNeighborhoods.some(neighborhood => {
                    const neighborhoodLower = neighborhood.toLowerCase();

                    // Check in venue neighborhoods first
                    if (venue.Neighborhoods && venue.Neighborhoods.length > 0) {
                        return venue.Neighborhoods.some(venueNeighborhood => {
                            const venueNeighborhoodName = getNeighborhoodName(venueNeighborhood);
                            return venueNeighborhoodName.toLowerCase() === neighborhoodLower;
                        });
                    }

                    // Fallback to attributes
                    return venueAttributes.includes(neighborhoodLower);
                });

            return hasSelectedAttributes && hasSelectedNeighborhoods;
        });
    };

    // Update filtered venues whenever selections change
    useEffect(() => {
        setFilteredVenues(filterVenuesSimple());
    }, [venues, selectedSpaces, selectedNeighborhoods]);

    // Fetch venue data
    useEffect(() => {
        if (!initialVenues) {
            setIsLoading(true);
            FetchAllVenues(100, null, true)
                .then(({ venues: fetchedVenues }) => {
                    setVenues(fetchedVenues);
                })
                .catch(error => {
                    console.error('Error fetching venues:', error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [initialVenues]);

    // Calculate available attributes and valid combinations
    useEffect(() => {
        if (!venues?.length) return;

        // Calculate available attributes
        const attributes: { [key: string]: number } = {};
        venues.forEach(venue => {
            Object.entries(venue.Attributes).forEach(([attr, value]) => {
                if (value) {
                    attributes[attr] = (attributes[attr] || 0) + 1;
                }
            });
        });
        setAvailableAttributes(attributes);

        // Calculate valid combinations
        const combinations = new Set<string>();
        venues.forEach(venue => {
            const validAttrs = Object.entries(venue.Attributes)
                .filter(([_, value]) => value)
                .map(([attr, _]) => attr.toLowerCase());

            // Create all possible pairs
            for (let i = 0; i < validAttrs.length; i++) {
                for (let j = i + 1; j < validAttrs.length; j++) {
                    const pair = validAttrs[i] < validAttrs[j]
                        ? `${validAttrs[i]}-${validAttrs[j]}`
                        : `${validAttrs[j]}-${validAttrs[i]}`;
                    combinations.add(pair);
                }
            }
        });
        setValidCombinations(combinations);
    }, [venues]);

    // Fetch metadata for attributes only (not neighborhoods)
    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const metadata = await fetchAllVenueMetadata();

                // Set attributes (excluding hardcoded locations)
                const attributes = metadata.possibleAttributes.filter(
                    attr => !hardcodedLocations.some(loc => loc.toLowerCase() === attr.toLowerCase())
                );
                setPossibleAttributes(attributes);
            } catch (error) {
                console.error('Error fetching venue metadata:', error);
            }
        };

        fetchMetadata();
    }, []);

    // Handle date selection
    const handleDateChange = (newDate: Date) => {
        setDate(newDate);
        setIsDatePickerOpen(false);

        // Track date selection
        trackEvent('search_modal_date_selected', {
            date: newDate.toISOString().split('T')[0],
            is_future: newDate > new Date(),
            source: 'standalone_search_modal'
        });
    };

    // Handle neighborhood selection with tracking
    const handleNeighborhoodSelect = (neighborhood: string) => {
        setSelectedNeighborhoods(prev => {
            // Check if already selected
            const isSelected = prev.includes(neighborhood);

            const newNeighborhoods = isSelected
                ? prev.filter(n => n !== neighborhood)
                : [...prev, neighborhood];

            // Track neighborhood selection/deselection
            trackEvent('search_modal_neighborhood_selected', {
                neighborhood: neighborhood,
                action: isSelected ? 'removed' : 'added',
                total_selected: newNeighborhoods.length,
                source: 'standalone_search_modal'
            });

            return newNeighborhoods;
        });
    };

    // Handle attribute selection with tracking
    const handleAttributeSelect = (attribute: string) => {
        console.log('Attribute selected:', attribute);

        // Handle as neighborhood if it's in the hardcoded locations
        if (hardcodedLocations.some(loc => loc.toLowerCase() === attribute.toLowerCase())) {
            handleNeighborhoodSelect(attribute);
            return;
        }

        // Otherwise handle as space attribute
        setSelectedSpaces(prev => {
            const isSelected = prev.some(item => item.toLowerCase() === attribute.toLowerCase());

            const newSpaces = isSelected
                ? prev.filter(attr => attr.toLowerCase() !== attribute.toLowerCase())
                : [...prev, attribute];

            // Track attribute selection/deselection
            trackEvent('search_modal_attribute_selected', {
                attribute: attribute,
                action: isSelected ? 'removed' : 'added',
                total_selected: newSpaces.length,
                source: 'standalone_search_modal'
            });

            return newSpaces;
        });
    };

    // Simple check if event type is selectable
    const isEventTypeSelectable = (type: string): boolean => {
        return true; // Always allow event type selection regardless of location
    };

    // Simple filtered neighborhoods list - just return hardcoded locations
    const getFilteredNeighborhoods = () => {
        return hardcodedLocations;
    };

    const filteredEventTypes = nonLocationTypes;
    const filteredNeighborhoodsList = getFilteredNeighborhoods();

    // Handler for search button
    const handleSearchClick = () => {
        const searchParams = new URLSearchParams();
        if (date) {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;
            searchParams.set('date', dateString);
        }
        if (time) searchParams.set('time', time);
        if (partySize) searchParams.set('size', partySize);

        // Add attributes if any selected
        if (selectedSpaces.length > 0) {
            searchParams.set('attributes', selectedSpaces.join(','));
        }

        // Add neighborhoods as a separate parameter
        if (selectedNeighborhoods.length > 0) {
            searchParams.set('neighborhoods', selectedNeighborhoods.join(','));
        }

        // Track search submission
        trackEvent('search_modal_submitted', {
            date: date ? date.toISOString().split('T')[0] : null,
            time: time,
            party_size: partySize,
            attributes: selectedSpaces,
            neighborhoods: selectedNeighborhoods,
            matching_venues: filteredVenues.length,
            search_params: searchParams.toString(),
            source: 'standalone_search_modal'
        });

        router.push(`/search?${searchParams.toString()}`);
        onClose();
    };

    const handleSearch = () => {
        handleSearchClick();
    };

    const handleClear = () => {
        // Track clear action before resetting
        trackEvent('search_modal_filters_cleared', {
            had_date: date !== null,
            had_time: time !== null,
            party_size: partySize,
            had_attributes: selectedSpaces.length > 0,
            had_neighborhoods: selectedNeighborhoods.length > 0,
            source: 'standalone_search_modal'
        });

        // Reset all local state
        setDate(dayjs().add(1, 'week').toDate());
        setTime('19:00');
        setPartySize('25');
        setSelectedSpaces([]);
        setSelectedNeighborhoods([]);
    };

    // Track when modal opens
    const [modalOpenTime, setModalOpenTime] = useState<Date | null>(null);

    // Track when modal is opened
    useEffect(() => {
        if (isOpen) {
            setModalOpenTime(new Date());
            trackEvent('search_modal_opened', {
                timestamp: new Date().toISOString(),
                source: 'standalone_search_modal'
            });
        }
    }, [isOpen]);

    // Handle modal close with tracking
    const handleClose = () => {
        if (modalOpenTime) {
            const timeSpentMs = new Date().getTime() - modalOpenTime.getTime();
            trackEvent('search_modal_abandoned', {
                time_spent_seconds: Math.round(timeSpentMs / 1000),
                had_filter_changes: selectedSpaces.length > 0 || selectedNeighborhoods.length > 0,
                search_criteria: {
                    date: date ? date.toDateString() : null,
                    time: time,
                    people: partySize,
                    event_types: selectedSpaces,
                    neighborhoods: selectedNeighborhoods
                },
                source: 'standalone_search_modal'
            });
        }
        setModalOpenTime(null);
        onClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="search-options-modal"
            aria-describedby="modal-with-search-options"
        >
            <Box
                sx={{
                    position: { xs: 'fixed', sm: 'absolute' },
                    top: { xs: 0, sm: '50%' },
                    left: { xs: 0, sm: '50%' },
                    transform: { xs: 'none', sm: 'translate(-50%, -50%)' },
                    width: { xs: '100%', sm: '85%', md: '75%', lg: '65%' },
                    maxWidth: { xs: '100%', sm: 650, md: 800, lg: 900 },
                    height: { xs: '100vh', sm: '75vh' },
                    maxHeight: { xs: '100vh', sm: 'calc(90vh - 40px)', md: '90vh' },
                    overflow: 'auto',
                    bgcolor: '#1A1B23',
                    color: 'white',
                    boxShadow: { xs: 'none', sm: '0 8px 32px rgba(224, 177, 241, 0.3)' },
                    pt: { xs: 2.5, sm: 3, md: 3 },
                    pr: { xs: 2.5, sm: 3, md: 3 },
                    pl: { xs: 2.5, sm: 3, md: 3 },
                    pb: { xs: 0, sm: 3, md: 3 },
                    borderRadius: { xs: 0, sm: 3, md: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Close Button */}
                <Button
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        padding: 0,
                        backgroundColor: 'rgba(224, 177, 241, 0.1)',
                        color: '#C0C0DD',
                        zIndex: 10,
                        '&:hover': {
                            backgroundColor: 'rgba(224, 177, 241, 0.3)',
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </Button>

                {isLoading ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px'
                    }}>
                        <CircularProgress sx={{ color: '#C0C0DD' }} />
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        gap: { xs: 1.5, sm: 2, md: 2 }
                    }}>
                        {/* Form Container - Three column layout for base inputs */}
                        <Box sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                            gap: { xs: 3, sm: 2, md: 2 },
                            mb: { xs: 3, sm: 2, md: 2 }
                        }}>
                            {/* Date Input */}
                            <Box sx={{ width: '100%' }}>
                                <Typography
                                    variant="h6"
                                    fontFamily='Inter'
                                    sx={{
                                        mb: 0.5,
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.95rem' }
                                    }}
                                >
                                    When's the big day?
                                </Typography>
                                <Box
                                    onClick={() => {
                                        setIsDatePickerOpen(true);
                                        trackEvent('search_modal_opened_date_picker', {
                                            source: 'standalone_search_modal'
                                        });
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        p: { xs: 1.25, md: 1.25 },
                                        borderRadius: '8px',
                                        bgcolor: 'rgba(224, 177, 241, 0.05)',
                                        border: '1px solid rgba(224, 177, 241, 0.3)',
                                        WebkitTapHighlightColor: 'transparent',
                                        touchAction: 'manipulation',
                                        userSelect: 'none',
                                        minHeight: { xs: '42px', md: '42px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        zIndex: 1,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(224, 177, 241, 0.1)',
                                            borderColor: 'rgba(224, 177, 241, 0.4)',
                                        },
                                        '&:active': {
                                            bgcolor: 'rgba(224, 177, 241, 0.15)',
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.85rem' }, fontWeight: 500 }}>
                                        {date ? date.toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        }) : 'Select a date'}
                                    </Typography>
                                </Box>
                                {isDatePickerOpen && (
                                    <Box
                                        className="date-picker-container"
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            position: 'absolute',
                                            zIndex: 1000,
                                            mt: 1,
                                            width: { xs: '85%', sm: '80%', md: '45%' },
                                            maxWidth: '400px',
                                            bgcolor: '#1A1B23',
                                            borderRadius: '12px',
                                            border: '2px solid #C0C0DD',
                                            boxShadow: '0 12px 24px rgba(224, 177, 241, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)',
                                            backdropFilter: 'blur(8px)',
                                            isolation: 'isolate'
                                        }}
                                    >
                                        <SmallCustomCalendar
                                            selectedDate={date || new Date()}
                                            minDate={new Date()}
                                            onDateChange={handleDateChange}
                                        />
                                    </Box>
                                )}
                            </Box>

                            {/* Time Input */}
                            <Box sx={{ width: '100%' }}>
                                <Typography
                                    variant="h6"
                                    fontFamily='Inter'
                                    sx={{
                                        mb: 0.5,
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.95rem' }
                                    }}
                                >
                                    What time is the party?
                                </Typography>
                                <Select
                                    value={time}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const newTime = e.target.value as string;
                                        setTime(newTime);

                                        // Track time selection
                                        trackEvent('search_modal_time_selected', {
                                            time: newTime,
                                            hour: parseInt(newTime.split(':')[0]),
                                            source: 'standalone_search_modal'
                                        });
                                    }}
                                    sx={{
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(224, 177, 241, 0.05)',
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.3)',
                                            borderWidth: '1px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.4)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.6)',
                                            borderWidth: '1px'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: '#C0C0DD'
                                        },
                                        height: { xs: '42px', md: '42px' },
                                        fontSize: { xs: '0.85rem', md: '0.85rem' },
                                        fontWeight: 500,
                                        padding: '0 12px',
                                        WebkitTapHighlightColor: 'transparent',
                                        touchAction: 'manipulation'
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#1A1B23',
                                                color: 'white',
                                                borderRadius: '10px',
                                                maxHeight: '250px',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                    padding: '6px 12px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(224, 177, 241, 0.15)'
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'rgba(224, 177, 241, 0.3)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <MenuItem key={i} value={`${i}:00`}>
                                            {dayjs().hour(i).format('h:00 A')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            {/* Party Size Input */}
                            <Box sx={{ width: '100%' }}>
                                <Typography
                                    variant="h6"
                                    fontFamily='Inter'
                                    sx={{
                                        mb: 0.5,
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.95rem' }
                                    }}
                                >
                                    Around how many people?
                                </Typography>
                                <Select
                                    value={partySize}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const newSize = e.target.value as string;
                                        setPartySize(newSize);

                                        // Track party size selection
                                        trackEvent('search_modal_party_size_selected', {
                                            party_size: newSize,
                                            source: 'standalone_search_modal'
                                        });
                                    }}
                                    sx={{
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(224, 177, 241, 0.05)',
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.3)',
                                            borderWidth: '1px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.4)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(224, 177, 241, 0.6)',
                                            borderWidth: '1px'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: '#C0C0DD'
                                        },
                                        height: { xs: '42px', md: '42px' },
                                        fontSize: { xs: '0.85rem', md: '0.85rem' },
                                        fontWeight: 500,
                                        padding: '0 12px',
                                        WebkitTapHighlightColor: 'transparent',
                                        touchAction: 'manipulation'
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#1A1B23',
                                                color: 'white',
                                                borderRadius: '10px',
                                                maxHeight: '250px',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                    padding: '6px 12px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(224, 177, 241, 0.15)'
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'rgba(224, 177, 241, 0.3)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((num) => (
                                        <MenuItem key={num} value={num.toString()}>
                                            {num} people
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>

                        {/* Mobile filters */}
                        <Box sx={{
                            display: { xs: 'flex', md: 'none' },
                            width: '100%',
                            mt: { xs: 2, sm: 1 },
                            mb: { xs: 3, sm: 1 },
                            flex: { xs: 1, sm: 'auto' },
                            flexDirection: 'column',
                            paddingBottom: { xs: 20, sm: 0 }
                        }}>
                            <Typography
                                variant="h6"
                                fontFamily='Inter'
                                sx={{
                                    mb: { xs: 1.5, sm: 0.5 },
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(224, 177, 241, 0.2)',
                                    pb: 0.5
                                }}
                            >
                                <span>Filters</span>
                            </Typography>

                            {/* Mobile filter chips */}
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'visible'
                            }}>
                                {/* Event Type */}
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        mb: 1,
                                        mt: 1
                                    }}
                                >
                                    Event Type
                                </Typography>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 0.5,
                                    mb: 2
                                }}>
                                    {filteredEventTypes.map((type) => {
                                        const isSelectable = isEventTypeSelectable(type) || selectedSpaces.includes(type);
                                        return (
                                            <FormControlLabel
                                                key={type}
                                                control={
                                                    <Checkbox
                                                        checked={selectedSpaces.includes(type)}
                                                        onChange={() => isSelectable && handleAttributeSelect(type)}
                                                        disabled={!isSelectable}
                                                        sx={{
                                                            color: isSelectable ? 'rgba(224, 177, 241, 0.6)' : 'rgba(224, 177, 241, 0.3)',
                                                            padding: '2px',
                                                            '&.Mui-checked': {
                                                                color: '#C0C0DD',
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: 'rgba(224, 177, 241, 0.2)',
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                fontSize: '0.9rem',
                                                            }
                                                        }}
                                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                    />
                                                }
                                                label={type}
                                                sx={{
                                                    margin: 0,
                                                    marginRight: 0,
                                                    minHeight: '24px',
                                                    alignItems: 'center',
                                                    opacity: isSelectable ? 1 : 0.5,
                                                    cursor: isSelectable ? 'pointer' : 'not-allowed',
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.75rem',
                                                        color: 'white',
                                                        fontWeight: selectedSpaces.includes(type) ? 600 : 400,
                                                        lineHeight: 1.2,
                                                        paddingTop: 0,
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </Box>

                                {/* Locations */}
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        mb: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <span>Locations</span>
                                </Typography>
                                <Box sx={{
                                    height: 'auto',
                                    overflow: 'visible',
                                    pb: 2,
                                    mb: 2,
                                    position: 'relative'
                                }}>
                                    <FormGroup sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: 0.5,
                                        paddingBottom: 3
                                    }}>
                                        {filteredNeighborhoodsList.map((neighborhood, index) => {
                                            return (
                                                <FormControlLabel
                                                    key={`neighborhood-${index}`}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedNeighborhoods.includes(neighborhood)}
                                                            onChange={() => handleNeighborhoodSelect(neighborhood)}
                                                            sx={{
                                                                color: 'rgba(224, 177, 241, 0.6)',
                                                                padding: '2px',
                                                                '&.Mui-checked': {
                                                                    color: '#C0C0DD',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '0.9rem',
                                                                }
                                                            }}
                                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                        />
                                                    }
                                                    label={neighborhood}
                                                    sx={{
                                                        margin: 0,
                                                        marginRight: 0,
                                                        minHeight: '24px',
                                                        alignItems: 'center',
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '0.75rem',
                                                            color: 'white',
                                                            fontWeight: selectedNeighborhoods.includes(neighborhood) ? 600 : 400,
                                                            lineHeight: 1.2,
                                                            paddingTop: 0,
                                                            maxWidth: '100%',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                </Box>
                            </Box>
                        </Box>

                        {/* Desktop Layout for Locations - Show as a grid of chips below the two columns */}
                        <Box sx={{
                            display: { xs: 'none', md: 'flex' },
                            width: '100%',
                            mt: 2,
                            flex: 1,
                            flexDirection: 'column'
                        }}>
                            {/* Section title for filters with compact layout */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                                pb: 0.5,
                                borderBottom: '1px solid rgba(224, 177, 241, 0.2)',
                            }}>
                                <Typography
                                    variant="h5"
                                    fontFamily='Inter'
                                    sx={{
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                    }}
                                >
                                    Filters
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.8rem',
                                        color: '#C0C0DD',
                                        fontWeight: 500
                                    }}
                                >
                                    {filteredVenues.length || 0} venues match
                                </Typography>
                            </Box>

                            {/* Three column layout for filters */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                gap: 2,
                                mb: 1.5,
                                flex: 1
                            }}>
                                {/* Event Types */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontFamily='Inter'
                                        sx={{
                                            mb: 0.5,
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        Event Type
                                    </Typography>
                                    <FormGroup sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        columnGap: 1,
                                        rowGap: 0.25
                                    }}>
                                        {filteredEventTypes.map((type) => {
                                            const isSelectable = isEventTypeSelectable(type) || selectedSpaces.includes(type);
                                            return (
                                                <FormControlLabel
                                                    key={type}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedSpaces.includes(type)}
                                                            onChange={() => isSelectable && handleAttributeSelect(type)}
                                                            disabled={!isSelectable}
                                                            sx={{
                                                                color: isSelectable ? 'rgba(224, 177, 241, 0.6)' : 'rgba(224, 177, 241, 0.3)',
                                                                padding: '2px',
                                                                '&.Mui-checked': {
                                                                    color: '#C0C0DD',
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: 'rgba(224, 177, 241, 0.2)',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '0.9rem',
                                                                }
                                                            }}
                                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                        />
                                                    }
                                                    label={type}
                                                    sx={{
                                                        margin: 0,
                                                        marginRight: 0,
                                                        minHeight: '24px',
                                                        alignItems: 'center',
                                                        opacity: isSelectable ? 1 : 0.5,
                                                        cursor: isSelectable ? 'pointer' : 'not-allowed',
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '0.75rem',
                                                            color: 'white',
                                                            fontWeight: selectedSpaces.includes(type) ? 600 : 400,
                                                            lineHeight: 1.2,
                                                            paddingTop: 0
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                </Box>

                                {/* Locations - First Column */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontFamily='Inter'
                                        sx={{
                                            mb: 0.5,
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        Locations
                                    </Typography>
                                    <Box sx={{
                                        maxHeight: { xs: '200px', sm: '220px', md: '250px' },
                                        overflowY: 'auto',
                                        pr: 1,
                                        '&::-webkit-scrollbar': {
                                            width: '4px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'rgba(224, 177, 241, 0.05)',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(224, 177, 241, 0.2)',
                                            borderRadius: '4px',
                                        }
                                    }}>
                                        <FormGroup sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            columnGap: 1,
                                            rowGap: 0.25
                                        }}>
                                            {filteredNeighborhoodsList.slice(0, Math.ceil(filteredNeighborhoodsList.length / 2)).map((neighborhood, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`neighborhood-col1-${index}`}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedNeighborhoods.includes(neighborhood)}
                                                                onChange={() => handleNeighborhoodSelect(neighborhood)}
                                                                sx={{
                                                                    color: 'rgba(224, 177, 241, 0.6)',
                                                                    padding: '2px',
                                                                    '&.Mui-checked': {
                                                                        color: '#C0C0DD',
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: '0.9rem',
                                                                    }
                                                                }}
                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                            />
                                                        }
                                                        label={neighborhood}
                                                        sx={{
                                                            margin: 0,
                                                            marginRight: 0,
                                                            minHeight: '24px',
                                                            alignItems: 'center',
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.75rem',
                                                                color: 'white',
                                                                fontWeight: selectedNeighborhoods.includes(neighborhood) ? 600 : 400,
                                                                lineHeight: 1.2,
                                                                paddingTop: 0,
                                                                maxWidth: '100%',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    </Box>
                                </Box>

                                {/* Locations - Second Column - Only show on larger screens */}
                                <Box sx={{ display: { md: 'none', lg: 'block' } }}>
                                    <Typography
                                        variant="h6"
                                        fontFamily='Inter'
                                        sx={{
                                            mb: 0.5,
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        &nbsp;
                                    </Typography>
                                    <Box sx={{
                                        maxHeight: { xs: '200px', sm: '220px', md: '250px' },
                                        overflowY: 'auto',
                                        pr: 1,
                                        '&::-webkit-scrollbar': {
                                            width: '4px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'rgba(224, 177, 241, 0.05)',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(224, 177, 241, 0.2)',
                                            borderRadius: '4px',
                                        }
                                    }}>
                                        <FormGroup sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            columnGap: 1,
                                            rowGap: 0.25
                                        }}>
                                            {filteredNeighborhoodsList.slice(Math.ceil(filteredNeighborhoodsList.length / 2)).map((neighborhood, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`neighborhood-col2-${index}`}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedNeighborhoods.includes(neighborhood)}
                                                                onChange={() => handleNeighborhoodSelect(neighborhood)}
                                                                sx={{
                                                                    color: 'rgba(224, 177, 241, 0.6)',
                                                                    padding: '2px',
                                                                    '&.Mui-checked': {
                                                                        color: '#C0C0DD',
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: '0.9rem',
                                                                    }
                                                                }}
                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                            />
                                                        }
                                                        label={neighborhood}
                                                        sx={{
                                                            margin: 0,
                                                            marginRight: 0,
                                                            minHeight: '24px',
                                                            alignItems: 'center',
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.75rem',
                                                                color: 'white',
                                                                fontWeight: selectedNeighborhoods.includes(neighborhood) ? 600 : 400,
                                                                lineHeight: 1.2,
                                                                paddingTop: 0,
                                                                maxWidth: '100%',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Search Button - Full Width at Bottom */}
                        <Box sx={{
                            width: '100%',
                            mt: { xs: 'auto', sm: 2, md: 2 },
                            pt: { xs: 1.5, sm: 1.5 },
                            display: 'flex',
                            justifyContent: 'space-between',
                            borderTop: '1px solid rgba(224, 177, 241, 0.2)',
                            paddingBottom: { xs: 3, sm: 6 },
                            position: { xs: 'fixed', sm: 'static' },
                            bottom: { xs: 0, sm: 'auto' },
                            left: { xs: 0, sm: 'auto' },
                            right: { xs: 0, sm: 'auto' },
                            backgroundColor: '#1A1B23',
                            zIndex: 5,
                            px: { xs: 2.5, sm: 0 },
                        }}>
                            <Button
                                onClick={handleClear}
                                sx={{
                                    backgroundColor: 'transparent',
                                    color: '#C0C0DD',
                                    textTransform: 'none',
                                    padding: { xs: '6px 14px', sm: '6px 16px', md: '6px 16px' },
                                    borderRadius: '6px',
                                    fontSize: { xs: '0.85rem', sm: '0.85rem', md: '0.85rem' },
                                    fontWeight: 600,
                                    fontFamily: 'Inter',
                                    border: '1px solid rgba(224, 177, 241, 0.4)',
                                    minWidth: '90px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                        borderColor: 'rgba(224, 177, 241, 0.6)',
                                    },
                                }}
                            >
                                Clear All
                            </Button>

                            <Button
                                onClick={handleSearch}
                                sx={{
                                    backgroundColor: '#C0C0DD',
                                    color: '#1A1B23',
                                    textTransform: 'none',
                                    padding: { xs: '6px 14px', sm: '6px 16px', md: '6px 16px' },
                                    borderRadius: '6px',
                                    fontSize: { xs: '0.85rem', sm: '0.85rem', md: '0.85rem' },
                                    fontWeight: 600,
                                    fontFamily: 'Inter',
                                    minWidth: { xs: '180px', sm: '200px' },
                                    '&:hover': {
                                        backgroundColor: '#d9a9eb',
                                    },
                                    '&:active': {
                                        backgroundColor: '#c99ce0',
                                        transform: 'scale(0.98)'
                                    }
                                }}
                            >
                                View Venues
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default SearchModal;
